import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { FileSpreadsheet, Download, Search, Filter, Trash2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useAuth } from '../../contexts/AuthContext';

interface StudentProgramListProps {
    programType: 'engineering' | 'imat' | 'license' | 'architecture' | 'polito' | 'polimi' | 'san-raffaele' | 'humanitas' | 'cattolica' | 'biomedico' | 'unicamillus' |
    'sapienza' | 'milano-statale' | 'pavia' | 'bologna' | 'padova' | 'tor-vergata' | 'turin' | 'bicocca' | 'federico' | 'parma' | 'messina' | 'vanvitelli' | 'bari' | 'catania' | 'marche' | 'cagliari' | 'siena-dentistry' | 'sapienza-dentistry' | 'bologna-vetmed';
}

const StudentProgramList: React.FC<StudentProgramListProps> = ({ programType }) => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [students, setStudents] = useState<any[]>([]);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchStudents();
    }, [programType]);

    const getPageTitle = () => {
        switch (programType) {
            case 'engineering': return 'Mühendislik Öğrencileri';
            case 'polito': return 'Politecnico di Torino Öğrencileri';
            case 'polimi': return 'Politecnico di Milano Öğrencileri';
            case 'imat': return 'IMAT Öğrencileri';
            case 'license': return 'Lisans Öğrencileri';
            case 'architecture': return 'Mimarlık Öğrencileri';
            case 'san-raffaele': return 'San Raffaele Öğrencileri';
            case 'humanitas': return 'Humanitas Öğrencileri';
            case 'cattolica': return 'Cattolica Öğrencileri';
            case 'biomedico': return 'Campus Bio-Medico Öğrencileri';
            case 'unicamillus': return 'UniCamillus Öğrencileri';

            // IMAT Public Universities
            case 'sapienza': return 'La Sapienza Öğrencileri';
            case 'milano-statale': return 'Milano Statale Öğrencileri';
            case 'pavia': return 'Pavia Öğrencileri';
            case 'bologna': return 'Bologna Öğrencileri';
            case 'padova': return 'Padova Öğrencileri';
            case 'tor-vergata': return 'Tor Vergata Öğrencileri';
            case 'turin': return 'Turin (Torino) Öğrencileri';
            case 'bicocca': return 'Milano Bicocca Öğrencileri';
            case 'federico': return 'Federico II Öğrencileri';
            case 'parma': return 'Parma Öğrencileri';
            case 'messina': return 'Messina Öğrencileri';
            case 'vanvitelli': return 'Luigi Vanvitelli Öğrencileri';
            case 'bari': return 'Bari Öğrencileri';
            case 'catania': return 'Catania Öğrencileri';
            case 'marche': return 'Marche (Ancona) Öğrencileri';
            case 'cagliari': return 'Cagliari Öğrencileri';
            case 'siena-dentistry': return 'Siena (Diş Hekimliği) Öğrencileri';
            case 'sapienza-dentistry': return 'La Sapienza (Diş Hekimliği) Öğrencileri';
            case 'bologna-vetmed': return 'Bologna (Veterinerlik) Öğrencileri';

            default: return 'Öğrenci Listesi';
        }
    };

    const fetchStudents = async () => {
        setLoading(true);
        try {
            // Fetch all operation students
            let operationStudentsQuery = supabase
                .from('operation_students')
                .select('*');
            if (user?.branchId) operationStudentsQuery = operationStudentsQuery.eq('branch_id', user.branchId);
            const { data: opStudents } = await operationStudentsQuery;

            // Fetch all regular students with leads
            let regularStudentsQuery = supabase
                .from('students')
                .select(`
          *,
          leads (
            interested_programs
          )
        `);
            if (user?.branchId) regularStudentsQuery = regularStudentsQuery.eq('branch_id', user.branchId);
            const { data: regStudents } = await regularStudentsQuery;

            let allStudents = [
                ...(opStudents || []).map(s => ({ ...s, type: 'operation' })),
                ...(regStudents || []).map(s => ({ ...s, type: 'regular' }))
            ];

            // Filter based on programType
            const filtered = allStudents.filter(student => {
                let keywords: string[] = [];

                switch (programType) {
                    case 'engineering':
                        keywords = ['mühendislik', 'engineering', 'eng'];
                        break;
                    case 'polito':
                        keywords = ['polito', 'torino', 'turin'];
                        break;
                    case 'polimi':
                        keywords = ['polimi', 'milano', 'milan'];
                        break;
                    case 'imat':
                        keywords = ['imat', 'tıp', 'medicine', 'med'];
                        break;
                    case 'license':
                        keywords = ['lisans', 'bachelor', 'undergrad'];
                        break;
                    case 'architecture':
                        keywords = ['mimarlık', 'architecture', 'arch', 'tasarım', 'design'];
                        break;
                    case 'san-raffaele':
                        keywords = ['san raffaele', 'unisr'];
                        break;
                    case 'humanitas':
                        keywords = ['humanitas', 'hunimed'];
                        break;
                    case 'cattolica':
                        keywords = ['cattolica', 'unicatt'];
                        break;
                    case 'biomedico':
                        keywords = ['biomedico', 'campus bio'];
                        break;
                    case 'unicamillus':
                        keywords = ['unicamillus', 'camillus'];
                        break;

                    // IMAT Public Universities filters
                    case 'sapienza': keywords = ['sapienza', 'rome', 'roma']; break;
                    case 'milano-statale': keywords = ['statale', 'milano', 'milan']; break;
                    case 'pavia': keywords = ['pavia']; break;
                    case 'bologna': keywords = ['bologna']; break;
                    case 'padova': keywords = ['padova', 'padua']; break;
                    case 'tor-vergata': keywords = ['tor vergata', 'vergata']; break;
                    case 'turin': keywords = ['turin', 'torino']; break;
                    case 'bicocca': keywords = ['bicocca']; break;
                    case 'federico': keywords = ['federico', 'naples', 'napoli']; break;
                    case 'parma': keywords = ['parma']; break;
                    case 'messina': keywords = ['messina']; break;
                    case 'vanvitelli': keywords = ['vanvitelli', 'campania']; break;
                    case 'bari': keywords = ['bari']; break;
                    case 'catania': keywords = ['catania']; break;
                    case 'marche': keywords = ['marche', 'ancona']; break;
                    case 'cagliari': keywords = ['cagliari']; break;
                    case 'siena-dentistry': keywords = ['siena', 'dentistry', 'diş']; break;
                    case 'sapienza-dentistry': keywords = ['sapienza', 'dentistry', 'diş']; break;
                    case 'bologna-vetmed': keywords = ['bologna', 'vet', 'veterinary', 'veteriner']; break;
                }

                // Check notes for Operation Students
                if (student.type === 'operation' && student.notes) {
                    const notesLower = student.notes.toLowerCase();
                    return keywords.some(k => notesLower.includes(k));
                }

                // Check leads for Regular Students
                if (student.type === 'regular' && student.leads && student.leads[0]?.interested_programs) {
                    const programs = student.leads[0].interested_programs;
                    return programs.some((p: string) => keywords.some(k => p.toLowerCase().includes(k)));
                }

                return false;
            });

            setStudents(filtered);
        } catch (error) {
            console.error('Error fetching students:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleExportExcel = () => {
        const ws = XLSX.utils.json_to_sheet(students.map(s => ({
            'Ad': s.first_name,
            'Soyad': s.last_name,
            'Email': s.email,
            'Telefon': s.phone,
            'Durum': s.status,
            'Notlar': s.notes || '-'
        })));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Students");
        XLSX.writeFile(wb, `${programType}_students.xlsx`);
    };

    const filteredStudents = students.filter(s =>
        `${s.first_name} ${s.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleDelete = async (id: string, type: string) => {
        if (!await window.appConfirm('Bu öğrenciyi silmek istediğinize emin misiniz?')) return;

        try {
            if (type === 'operation') {
                const { error } = await supabase
                    .from('operation_students')
                    .delete()
                    .eq('id', id);
                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('students')
                    .delete()
                    .eq('id', id);
                if (error) throw error;
            }

            setStudents(prev => prev.filter(s => s.id !== id));
        } catch (error) {
            console.error('Error deleting:', error);
            alert('Silinirken hata oluştu');
        }
    };

    return (
        <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
                        <FileSpreadsheet className="w-6 h-6 text-green-600" />
                        {getPageTitle()}
                    </h1>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                        Toplam {filteredStudents.length} öğrenci listeleniyor
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Ara..."
                            className="pl-9 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <button
                        onClick={handleExportExcel}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                        <Download className="w-4 h-4" />
                        Excel İndir
                    </button>
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                            <tr>
                                <th className="px-6 py-3 border-b border-r dark:border-gray-600">Ad Soyad</th>
                                <th className="px-6 py-3 border-b border-r dark:border-gray-600">İletişim</th>
                                <th className="px-6 py-3 border-b border-r dark:border-gray-600">Durum</th>
                                <th className="px-6 py-3 border-b border-r dark:border-gray-600 w-1/3">Notlar / Programlar</th>
                                <th className="px-6 py-3 border-b dark:border-gray-600 text-center">İşlem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {loading ? (
                                <tr><td colSpan={5} className="p-8 text-center text-gray-500">Yükleniyor...</td></tr>
                            ) : filteredStudents.length === 0 ? (
                                <tr><td colSpan={5} className="p-8 text-center text-gray-500">Kayıt bulunamadı.</td></tr>
                            ) : (
                                filteredStudents.map((student) => (
                                    <tr key={student.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                        <td className="px-6 py-4 font-medium text-gray-900 dark:text-white border-r dark:border-gray-700">
                                            {student.first_name} {student.last_name}
                                            {student.type === 'operation' && <span className="ml-2 px-2 py-0.5 text-[10px] bg-blue-100 text-blue-800 rounded-full">Operasyon</span>}
                                        </td>
                                        <td className="px-6 py-4 border-r dark:border-gray-700">
                                            <div className="flex flex-col">
                                                <span className="text-gray-900 dark:text-white">{student.email}</span>
                                                <span className="text-gray-500 text-xs">{student.phone}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 border-r dark:border-gray-700">
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${student.status === 'active' ? 'bg-green-100 text-green-800' :
                                                student.status === 'passive' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                {student.status || 'Aktif'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 border-r dark:border-gray-700 font-mono text-xs text-gray-600 dark:text-gray-300 whitespace-pre-wrap max-h-20 overflow-y-auto block">
                                            {(student.notes || (student.leads?.[0]?.interested_programs?.join(', '))) || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex items-center justify-center gap-2">
                                                <button className="text-blue-600 hover:text-blue-800 font-medium hidden">Detay</button>
                                                <button
                                                    onClick={() => handleDelete(student.id, student.type)}
                                                    className="p-1.5 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                                                    title="Sil"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default StudentProgramList;
